import { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '../../contexts/I18nContext';

// 自定义 Hook 用于支付状态轮询
export const usePaymentStatusPoller = ({ orderId, onStatusUpdate, maxAttempts = 15, interval = 3000 }) => {
  const { t } = useI18n();
  const [isPolling, setIsPolling] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('');
  const intervalRef = useRef(null);
  const quickSuccessTimeoutRef = useRef(null);
  const absoluteTimeoutRef = useRef(null);
  const isPollingRef = useRef(false);
  const attemptsRef = useRef(0);
  const consecutiveErrorsRef = useRef(0); // 连续错误计数
  
  // 使用 useCallback 优化函数引用，避免不必要的重新创建
  const pollPaymentStatus = useCallback(async () => {
    try {
      // 检查网络连接状态
      if (!navigator.onLine) {
        const offlineMessage = t('payment.poller.offline', 'Network connection lost');
        setMessage(offlineMessage);
        consecutiveErrorsRef.current += 1;
        return false; // 继续轮询，等待网络恢复
      }

      const response = await fetch(`/api/v2/payment/order/${orderId}/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        consecutiveErrorsRef.current += 1;
        
        // 处理不同的HTTP错误状态
        if (response.status === 401) {
          const authErrorMessage = t('payment.poller.auth_error', 'Authentication failed');
          setMessage(authErrorMessage);
          onStatusUpdate({ status: 'error', message: authErrorMessage });
          return true; // 停止轮询
        } else if (response.status === 404) {
          const notFoundMessage = t('payment.poller.not_found', 'Order not found');
          setMessage(notFoundMessage);
          // 404错误在多次尝试后应该停止
          if (consecutiveErrorsRef.current >= 3) {
            onStatusUpdate({ status: 'error', message: notFoundMessage });
            return true; // 停止轮询
          }
          return false; // 继续轮询，可能是延迟
        } else if (response.status >= 500) {
          const serverErrorMessage = t('payment.poller.server_error', 'Server error, retrying...');
          setMessage(serverErrorMessage);
          // 服务器错误在多次尝试后应该停止
          if (consecutiveErrorsRef.current >= 5) {
            onStatusUpdate({ status: 'error', message: serverErrorMessage });
            return true; // 停止轮询
          }
          return false; // 继续轮询
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // 验证响应数据结构
      if (!data || typeof data !== 'object') {
        consecutiveErrorsRef.current += 1;
        const invalidDataMessage = t('payment.poller.invalid_data', 'Invalid response data');
        setMessage(invalidDataMessage);
        if (consecutiveErrorsRef.current >= 3) {
          onStatusUpdate({ status: 'error', message: invalidDataMessage });
          return true; // 停止轮询
        }
        return false; // 继续轮询
      }
      
      // 重置连续错误计数
      consecutiveErrorsRef.current = 0;
      
      if (data.success) {
        const paymentStatus = data.status;
        const creditsAdded = data.creditsAdded || false;
        
        // 验证支付状态值
        if (!paymentStatus || typeof paymentStatus !== 'string') {
          const invalidStatusMessage = t('payment.poller.invalid_status', 'Invalid payment status');
          setMessage(invalidStatusMessage);
          return false; // 继续轮询
        }
        
        switch (paymentStatus) {
          case 'PAID':
            if (creditsAdded) {
              const successMessage = t('payment.poller.success', 'Payment completed successfully');
              setMessage(successMessage);
              onStatusUpdate({ status: 'completed', message: successMessage, data: data });
              return true; // 停止轮询
            } else {
              const processingMessage = t('payment.poller.processing', 'Payment confirmed, processing credits...');
              setMessage(processingMessage);
              return false; // 继续轮询
            }
          
          case 'FAILED':
          case 'CANCELLED':
            const failedMessage = t('payment.poller.failed', 'Payment failed');
            setMessage(failedMessage);
            onStatusUpdate({ status: 'failed', message: failedMessage, data: data });
            return true; // 停止轮询
          
          case 'PENDING':
          case 'PROCESSING':
            const pendingMessage = t('payment.poller.processing', 'Payment is being processed...');
            setMessage(pendingMessage);
            return false; // 继续轮询
          
          default:
            const unknownMessage = t('payment.poller.unknown', 'Unknown payment status');
            setMessage(unknownMessage);
            return false; // 继续轮询
        }
      } else {
        consecutiveErrorsRef.current += 1;
        const errorMessage = data.message || t('payment.poller.error', 'Error checking payment status');
        setMessage(errorMessage);
        // API返回success=false时，在多次尝试后应该停止
        if (consecutiveErrorsRef.current >= 5) {
          onStatusUpdate({ status: 'error', message: errorMessage });
          return true; // 停止轮询
        }
        return false; // 继续轮询
      }
    } catch (error) {
      console.error('Payment status polling error:', error);
      consecutiveErrorsRef.current += 1;
      const networkErrorMessage = t('payment.poller.network_error', 'Network error while checking payment status');
      setMessage(networkErrorMessage);
      // 网络错误在多次尝试后应该停止
      if (consecutiveErrorsRef.current >= 5) {
        onStatusUpdate({ status: 'error', message: networkErrorMessage });
        return true; // 停止轮询
      }
      return false; // 继续轮询
    }
  }, [orderId, t, onStatusUpdate]); // 添加依赖项
  // 轮询逻辑
  useEffect(() => {
    if (!orderId || !onStatusUpdate) {
      console.warn('PaymentStatusPoller: Missing required props');
      return;
    }

    let intervalId;
    let isActive = true; // 用于防止组件卸载后的状态更新
    
    const startPolling = async () => {
      // 初始化状态
      attemptsRef.current = 0;
      consecutiveErrorsRef.current = 0;
      
      // 立即执行第一次检查
      if (isActive) {
        const shouldStop = await pollPaymentStatus();
        if (shouldStop || !isActive) {
          return;
        }
        attemptsRef.current += 1;
      }
      
      // 设置定时轮询
      intervalId = setInterval(async () => {
        if (!isActive) {
          clearInterval(intervalId);
          return;
        }
        
        // 检查是否超过最大尝试次数
        if (attemptsRef.current >= maxAttempts) {
          const timeoutMessage = t('payment.poller.timeout', 'Payment verification timeout');
          setMessage(timeoutMessage);
          onStatusUpdate({ status: 'timeout', message: timeoutMessage });
          clearInterval(intervalId);
          return;
        }
        
        // 检查连续错误次数
        if (consecutiveErrorsRef.current >= 10) {
          const errorMessage = t('payment.poller.too_many_errors', 'Too many consecutive errors');
          setMessage(errorMessage);
          onStatusUpdate({ status: 'error', message: errorMessage });
          clearInterval(intervalId);
          return;
        }
        
        try {
          const shouldStop = await pollPaymentStatus();
          if (shouldStop) {
            clearInterval(intervalId);
            return;
          }
          attemptsRef.current += 1;
        } catch (error) {
          console.error('Polling error:', error);
          attemptsRef.current += 1;
        }
      }, interval);
    };
    
    startPolling();
    
    // 清理函数
    return () => {
      isActive = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [orderId, onStatusUpdate, maxAttempts, interval, pollPaymentStatus, t]);

  // 停止轮询的方法
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    isPollingRef.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (quickSuccessTimeoutRef.current) {
      clearTimeout(quickSuccessTimeoutRef.current);
      quickSuccessTimeoutRef.current = null;
    }
    if (absoluteTimeoutRef.current) {
      clearTimeout(absoluteTimeoutRef.current);
      absoluteTimeoutRef.current = null;
    }
  }, []);

  // 开始轮询
  const startPolling = useCallback(() => {
    if (isPolling || !orderId) return;
    
    // 验证orderId格式
    if (typeof orderId !== 'string' || orderId.trim() === '') {
      console.error('Invalid orderId provided to PaymentStatusPoller:', orderId);
      const errorMessage = t('payment.poller.invalid_order', 'Invalid order ID');
      setMessage(errorMessage);
      onStatusUpdate({ status: 'error', message: errorMessage });
      return;
    }
    
    setIsPolling(true);
    isPollingRef.current = true; // 同步更新 ref
    setAttempts(0);
    attemptsRef.current = 0; // 同步重置尝试次数 ref
    setMessage(t('payment.poller.starting', 'Starting payment verification...'));
    
    // 设置10秒绝对超时 - 这是真正的超时逻辑
    absoluteTimeoutRef.current = setTimeout(() => {
      if (isPollingRef.current) {
        const timeoutMessage = t('payment.poller.absolute_timeout', 'Payment verification timeout after 10 seconds');
        setMessage(timeoutMessage);
        onStatusUpdate({ status: 'timeout', message: timeoutMessage });
        stopPolling();
      }
    }, 10000); // 10秒绝对超时
    
    // 设置8秒快速成功提示 - 用户体验优化
    quickSuccessTimeoutRef.current = setTimeout(() => {
      // 使用 ref 检查当前状态，避免闭包问题
      if (isPollingRef.current) {
        const quickSuccessMessage = t('payment.poller.quickSuccess', 'Payment successful! Processing your credits...');
        setMessage(quickSuccessMessage);
        onStatusUpdate({ 
          status: 'quick_success', 
          message: quickSuccessMessage,
          data: { showQuickSuccess: true }
        });
        // 清理快速成功超时引用，但不停止轮询
        quickSuccessTimeoutRef.current = null;
      }
    }, 8000); // 8秒后显示快速成功提示，给绝对超时留2秒缓冲
    
    intervalRef.current = setInterval(async () => {
      // 检查轮询是否仍然活跃
      if (!isPollingRef.current) {
        return;
      }
      
      // 先更新尝试次数
      attemptsRef.current += 1;
      const currentAttempts = attemptsRef.current;
      setAttempts(currentAttempts);
      
      if (currentAttempts > maxAttempts) {
        const maxAttemptsTimeoutMessage = t('payment.poller.max_attempts_timeout', 'Payment verification timeout after maximum attempts');
        setMessage(maxAttemptsTimeoutMessage);
        onStatusUpdate({ status: 'timeout', message: maxAttemptsTimeoutMessage });
        stopPolling();
        return;
      }
      
      // 同步执行轮询检查，避免竞态条件
      try {
        const shouldStop = await pollPaymentStatus();
        if (shouldStop && isPollingRef.current) {
          // 如果轮询应该停止，清理所有超时
          if (quickSuccessTimeoutRef.current) {
            clearTimeout(quickSuccessTimeoutRef.current);
            quickSuccessTimeoutRef.current = null;
          }
          if (absoluteTimeoutRef.current) {
            clearTimeout(absoluteTimeoutRef.current);
            absoluteTimeoutRef.current = null;
          }
          stopPolling();
        }
      } catch (error) {
        console.error('Polling error:', error);
        // 轮询错误时，检查是否应该停止（避免无限重试）
        if (currentAttempts >= maxAttempts) {
          const errorMessage = t('payment.poller.max_retries', 'Maximum retry attempts reached');
          setMessage(errorMessage);
          onStatusUpdate({ status: 'error', message: errorMessage });
          stopPolling();
        }
        // 否则继续尝试
      }
    }, interval);
  }, [isPolling, orderId, t, onStatusUpdate, maxAttempts, interval, pollPaymentStatus, stopPolling]);

  // 清理函数
  useEffect(() => {
    return () => {
      // 组件卸载时强制停止轮询
      stopPolling();
      // 额外清理，确保没有遗漏的定时器
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (quickSuccessTimeoutRef.current) {
        clearTimeout(quickSuccessTimeoutRef.current);
        quickSuccessTimeoutRef.current = null;
      }
      if (absoluteTimeoutRef.current) {
        clearTimeout(absoluteTimeoutRef.current);
        absoluteTimeoutRef.current = null;
      }
      // 重置ref状态
      isPollingRef.current = false;
    };
  }, [stopPolling]);

  return {
    isPolling,
    attempts,
    message,
    startPolling,
    stopPolling
  };
};

// React 组件版本，用于在 JSX 中使用
const PaymentStatusPoller = ({ orderId, onStatusUpdate, maxAttempts = 30, interval = 2000 }) => {
  const { isPolling, startPolling, stopPolling } = usePaymentStatusPoller({
    orderId,
    onStatusUpdate,
    maxAttempts,
    interval
  });

  // 自动开始轮询
  useEffect(() => {
    if (orderId && !isPolling) {
      startPolling();
    }
  }, [orderId, isPolling, startPolling]);

  // 组件卸载时确保停止轮询
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // 不渲染任何内容，只负责轮询逻辑
  return null;
};

export default PaymentStatusPoller;
